/**
 * KeyboardShortcutsModal.tsx
 *
 * Keyboard Shortcuts Cheatsheet Modal for TaskFlow.
 * Displays categorised navigation, triage, and action hotkeys.
 */

import React from 'react';

export interface KeyboardShortcutsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutEntry {
  key: string;
  description: string;
}

const SHORTCUT_CATEGORIES: Array<{ title: string; shortcuts: ShortcutEntry[] }> = [
  {
    title: '🧭 Navigation & Views',
    shortcuts: [
      { key: 'B', description: 'Switch to Kanban Board View' },
      { key: 'C', description: 'Switch to Unified Calendar View' },
      { key: 'T', description: 'Switch to Timeline & Gantt View' },
      { key: 'W', description: 'Switch to WBS Tree View' },
      { key: 'Q', description: 'Switch to Triage Review Queue' },
    ],
  },
  {
    title: '⚡ Triage Mode Hotkeys',
    shortcuts: [
      { key: 'J / K', description: 'Next / Previous Card' },
      { key: '1 .. 5', description: 'Set Status: 1=Backlog, 2=In Progress, 3=Blocked, 4=Review, 5=Done' },
      { key: 'P', description: 'Cycle Priority (Low ➔ Normal ➔ Medium ➔ High ➔ Urgent)' },
      { key: 'D', description: 'Defer Due Date +1 Day' },
      { key: 'A', description: 'Archive Current Item' },
    ],
  },
  {
    title: '🚀 Global Actions & Creation',
    shortcuts: [
      { key: 'N', description: 'Create New Task' },
      { key: 'F', description: 'Focus Search & Bounded Query Bar' },
      { key: 'Cmd + K', description: 'Open Global Command Palette' },
      { key: 'Cmd + ? / ?', description: 'Open Keyboard Shortcuts Cheatsheet' },
      { key: 'Esc', description: 'Close Drawer / Dismiss Modal' },
    ],
  },
];

export const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        zIndex: 110,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '580px',
          backgroundColor: 'var(--theme-bg-modal, rgba(15, 23, 42, 0.96))',
          border: '1px solid var(--theme-border, rgba(255, 255, 255, 0.15))',
          borderRadius: '16px',
          padding: '24px',
          boxShadow: '0 20px 50px rgba(0, 0, 0, 0.7)',
          color: 'var(--theme-text-primary, #f8fafc)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '18px' }}>⌨️</span>
            <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0 }}>
              TaskFlow Keyboard Shortcuts
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--theme-text-muted, #94a3b8)', cursor: 'pointer', fontSize: '18px' }}
          >
            ✕
          </button>
        </div>

        {/* Shortcuts Categories */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {SHORTCUT_CATEGORIES.map((cat) => (
            <div key={cat.title}>
              <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--theme-accent, #818cf8)', marginBottom: '8px' }}>
                {cat.title}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {cat.shortcuts.map((s) => (
                  <div
                    key={s.key}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '12px',
                      padding: '4px 0',
                    }}
                  >
                    <span style={{ color: 'var(--theme-text-secondary, #cbd5e1)' }}>
                      {s.description}
                    </span>
                    <kbd
                      style={{
                        backgroundColor: 'var(--theme-bg-subtle, rgba(255, 255, 255, 0.08))',
                        border: '1px solid var(--theme-border-subtle, rgba(255, 255, 255, 0.1))',
                        borderRadius: '4px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: 'var(--theme-text-primary, #fff)',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
                      }}
                    >
                      {s.key}
                    </kbd>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
